import {
  createZapTimeSpentFromTimeSavedAtom,
  currentAccountIdAtom,
  newAssetIdentifierAtom,
  sessionAtom,
  canvasAtom,
  Zap,
} from "@zapier/canvas-state";
import { useEmitResourceConnectEvents } from "@zapier/observability";
import { useSetAtom, useAtomValue } from "jotai";
import { useCallback, useState } from "react";
import { Node } from "reactflow";

import { useDeleteNodes } from "../../hooks/useDeleteNodes";
import useUpdateNode from "../../hooks/useUpdateNode";

export function useSidebarEventHandlers({ node }: { node: Node }) {
  const session = useAtomValue(sessionAtom);
  const canvas = useAtomValue(canvasAtom);
  const currentAccountId = useAtomValue(currentAccountIdAtom);
  const { id } = node;
  const { emitEventOnZapConnect } = useEmitResourceConnectEvents({
    sessionId: session?.id,
    canvasId: canvas.id,
    currentAccountId: currentAccountId,
  });
  const { onNodesDelete } = useDeleteNodes();
  const updateNodeAndSave = useUpdateNode();
  const [selectedZap, setSelectedZap] = useState<any>(null);
  const setNewAssetIdentifier = useSetAtom(newAssetIdentifierAtom);
  const createZapTimeSpentFromTimeSaved = useSetAtom(
    createZapTimeSpentFromTimeSavedAtom,
  );

  const onDeleteStep = useCallback(() => {
    if (node) {
      onNodesDelete([node]);
    }
  }, [onNodesDelete, node]);

  const handleSubmit = useCallback(
    (selectedZap: Zap) => {
      setSelectedZap(selectedZap);
      // This is when you click the X button in the search input
      if (selectedZap === null) {
        return;
      }

      updateNodeAndSave({
        type: "zap",
        id,
        data: {
          zapId: selectedZap.id,
          isExpanded: true,
        },
      });

      setNewAssetIdentifier(`zap:${selectedZap.id}`);
      emitEventOnZapConnect(selectedZap.id);
      createZapTimeSpentFromTimeSaved(id, selectedZap.id);
    },
    [
      emitEventOnZapConnect,
      updateNodeAndSave,
      setNewAssetIdentifier,
      createZapTimeSpentFromTimeSaved,
      id,
    ],
  );
  const onRemoveZap = useCallback(() => {
    setSelectedZap(null);
    setNewAssetIdentifier(null);

    updateNodeAndSave({
      id,
      type: "default",
      data: {
        zapId: undefined,
        isExpanded: undefined,
      },
    });
  }, [updateNodeAndSave, setNewAssetIdentifier, id]);

  return {
    onRemoveZap,
    handleSubmit,
    onDeleteStep,
    selectedZap,
  };
}
